"use client"

import { useChains } from "@opensea/app-kit"
import type { TransactionIdentifier as GraphQLTransactionIdentifier } from "@opensea/graphql"
import { useEffect, useRef } from "react"
import { useTranslations } from "use-intl"
import { Button } from "../../../../../components/Button"
import { TransactionFullScreen } from "../../../../../components/TransactionFullScreen"
import { useTransactionStatus } from "../../../../../polychain/hooks/useTransactionStatus"
import { FlowActions } from "../../../../../stores/flowStore"

type TokenClaimStatusProps = {
  transactionIdentifier: GraphQLTransactionIdentifier
  tokenImageUrl: string | null
}

export function TokenClaimStatus({
  transactionIdentifier,
  tokenImageUrl,
}: TokenClaimStatusProps) {
  const t = useTranslations("wallet.TokenClaimStatus")
  const { getChain } = useChains()
  const hasCalledOnSuccess = useRef(false)

  const chain = getChain(transactionIdentifier.chain)
  const blockExplorer = chain?.blockExplorer

  const transactionStatus = useTransactionStatus({
    transaction: {
      chain: transactionIdentifier.chain,
      hash: transactionIdentifier.transactionHash,
    },
  })

  // Call onSuccess when transaction succeeds
  useEffect(() => {
    if (transactionStatus === "success" && !hasCalledOnSuccess.current) {
      hasCalledOnSuccess.current = true
      FlowActions.onSuccess()
    }
  }, [transactionStatus])

  if (transactionStatus === "pending") {
    return (
      <TransactionFullScreen
        body={t("pending.description")}
        images={tokenImageUrl ? [tokenImageUrl] : []}
        loading
        title={t("pending.title")}
      />
    )
  }

  const transactionUrl = blockExplorer?.transactionUrlTemplate.replace(
    ":hash",
    transactionIdentifier.transactionHash,
  )

  if (transactionStatus === "error") {
    return (
      <TransactionFullScreen
        body={t("failed.description")}
        bottom={
          <Button href={transactionUrl} target="_blank" variant="secondary">
            {t("viewTransaction", {
              explorerName: blockExplorer?.name ?? "",
            })}
          </Button>
        }
        images={tokenImageUrl ? [tokenImageUrl] : []}
        monochrome
        title={t("failed.title")}
      />
    )
  }

  // Success state
  return (
    <TransactionFullScreen
      bottom={
        <Button href={transactionUrl} target="_blank" variant="secondary">
          {t("viewTransaction", {
            explorerName: blockExplorer?.name ?? "",
          })}
        </Button>
      }
      confetti
      images={tokenImageUrl ? [tokenImageUrl] : []}
      title={t("success.title")}
    />
  )
}
